import escapeRegExp from "./escapeRegExp";

// Precomposed Latin, Greek and Cyrillic letters that canonically decompose into a base letter plus
// combining marks, e.g. 'é' -> 'e' + U+0301, 'ή' -> 'η' + U+0301, 'й' -> 'и' + U+0306.
const DECOMPOSABLE_LETTER_RANGES: [
    number,
    number
][] = [
    [192, 591], // Latin-1 Supplement, Latin Extended-A/B
    [880, 1023], // Greek and Coptic
    [1024, 1279], // Cyrillic
    [7680, 7935], // Latin Extended Additional
    [7936, 8191], // Greek Extended
];

const COMBINING_MARKS_REGEX = /\p{M}/gu;
const SINGLE_COMBINING_MARK_REGEX = /^\p{M}$/u;
const SINGLE_LETTER_REGEX = /^\p{L}$/u;

let diacriticVariants: Map<string, string> | null = null;

// Maps each lowercase base letter to every precomposed letter (any case) that folds to it.
function getDiacriticVariants(): Map<string, string> {
    if (!diacriticVariants) {
        diacriticVariants = new Map();
        for (const [start, end] of DECOMPOSABLE_LETTER_RANGES) {
            for (let codePoint = start; codePoint <= end; codePoint++) {
                const char = String.fromCodePoint(codePoint);
                const base = stripDiacritics(char);
                if (base !== char && SINGLE_LETTER_REGEX.test(base)) {
                    const key = base.toLowerCase();
                    diacriticVariants.set(key, (diacriticVariants.get(key) ?? "") + char);
                }
            }
        }
    }
    return diacriticVariants;
}

function stripDiacritics(char: string): string {
    return char.normalize("NFD").replace(COMBINING_MARKS_REGEX, "");
}

/**
 * Creates a case-insensitive RegExp that matches `keyword` at the start of a word.
 *
 * Group 1 captures the (possibly empty) word boundary and group 2 captures the match itself,
 * so callers using mark.js should pass `ignoreGroups: 1`.
 *
 * Word boundaries are Unicode-aware (unlike `\b`). Latin, Greek and Cyrillic letters match
 * regardless of diacritics in either the keyword or the text, combining-mark (NFD) forms are
 * accepted, and combining marks trailing the match are kept inside it.
 */
export default function createPrefixRegExp(keyword: string): RegExp {
    const variants = getDiacriticVariants();
    let pattern = "";
    let previousCharWasFolded = false;
    
    for (const char of keyword.normalize("NFC")) {
        if (previousCharWasFolded && SINGLE_COMBINING_MARK_REGEX.test(char)) {
            // Already covered by the trailing \p{M}* of the folded letter
            continue;
        }
        
        const lowerBase = stripDiacritics(char).toLowerCase();
        const baseVariants = SINGLE_LETTER_REGEX.test(lowerBase)
            ? variants.get(lowerBase)
            : undefined;
        if (baseVariants) {
            pattern += `[${lowerBase}${baseVariants}]\\p{M}*`;
            previousCharWasFolded = true;
        }
        else {
            pattern += escapeRegExp(char);
            previousCharWasFolded = false;
        }
    }
    
    if (!previousCharWasFolded) {
        pattern += "\\p{M}*";
    }
    
    return new RegExp(`(^|[^\\p{L}\\p{N}\\p{M}_])(${pattern})`, "gmiu");
}
